// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import {
  Shield,
  Archive,
  AlertTriangle,
  FileText,
  Clock,
  RefreshCw,
} from "lucide-react";

const DashboardTab = ({ gdprStatus, auditLogs, error, onRefresh, loading }) => {
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unbekannt";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return timestamp;
      }
      return date.toLocaleString("de-DE");
    } catch (error) {
      return timestamp;
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400 dark:text-red-500" />
            <div className="ml-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={onRefresh}
          disabled={loading}
          className="px-4 py-2 bg-primary-color text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center"
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
          />
          Daten aktualisieren
        </button>
      </div>

      {/* GDPR Status Overview */}
      {gdprStatus && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 p-6">
          <h3 className="text-lg font-medium mb-4 dark:text-slate-100">
            GDPR System Status
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-3 bg-primary-color-tint rounded-lg dark:border dark:border-slate-600">
              <span className="font-medium dark:text-slate-300">
                Compliance Score:
              </span>
              <div className="text-2xl font-bold primary-color">
                {gdprStatus.compliance_score}%
              </div>
            </div>
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg dark:border dark:border-slate-600">
              <span className="font-medium dark:text-slate-300">
                Ausstehende Löschungen:
              </span>
              <div className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                {gdprStatus.pending_deletions}
              </div>
            </div>
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg dark:border dark:border-slate-600">
              <span className="font-medium dark:text-slate-300">
                Aufbewahrungsrichtlinien:
              </span>
              <div className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">
                {gdprStatus.retention_policies}
              </div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg dark:border dark:border-slate-600">
              <span className="font-medium dark:text-slate-300">
                Letzter Cleanup:
              </span>
              <div className="text-lg font-semibold text-gray-600 dark:text-slate-300">
                {gdprStatus.last_cleanup
                  ? formatTimestamp(gdprStatus.last_cleanup)
                  : "Noch nicht durchgeführt"}
              </div>
            </div>
          </div>

          {/* Dashboard Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
            <div className="bg-primary-color-tint p-4 rounded-lg border dark:border-slate-600">
              <div className="flex items-center">
                <Shield className="h-8 w-8 primary-color" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                    VHP Benutzer
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                    {gdprStatus?.vhp_users?.total || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border dark:border-slate-600">
              <div className="flex items-center">
                <Archive className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                    Tenant Benutzer
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                    {gdprStatus?.tenant_users?.total || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border dark:border-slate-600">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                    Soft Deleted (VHP)
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                    {gdprStatus?.vhp_users?.soft_deleted || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border dark:border-slate-600">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-green-600 dark:text-green-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                    Audit Einträge
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                    {auditLogs ? auditLogs.length : "Nicht geladen"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border dark:border-slate-600">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">
                    Ausstehende Löschungen
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                    {gdprStatus?.pending_deletions || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Score Visualization */}
      {gdprStatus && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 p-6">
          <h3 className="text-lg font-medium mb-4 dark:text-slate-100">
            Compliance Score
          </h3>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-4">
                <div
                  className="bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${gdprStatus.compliance_score}%` }}
                ></div>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              {gdprStatus.compliance_score}%
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600 dark:text-slate-400">
            {gdprStatus.compliance_score >= 80
              ? "Hervorragend - Vollständig DSGVO-konform"
              : gdprStatus.compliance_score >= 60
                ? "Gut - Größtenteils konform"
                : gdprStatus.compliance_score >= 40
                  ? "Mittel - Verbesserungsbedarf"
                  : "Kritisch - Sofortige Maßnahmen erforderlich"}
          </div>
        </div>
      )}

      {/* Recent Activity - Only show if audit logs are available */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 p-6">
        <h3 className="text-lg font-medium mb-4 dark:text-slate-100">
          Kürzlich durchgeführte Aktionen
        </h3>
        {!auditLogs || auditLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-slate-400">
            <FileText className="h-12 w-12 mx-auto text-gray-300 dark:text-slate-600 mb-4" />
            <p className="text-sm">
              {!auditLogs
                ? "Audit-Logs werden geladen..."
                : "Keine Audit-Einträge verfügbar"}
            </p>
            {!auditLogs && (
              <button
                onClick={() => onRefresh()}
                className="mt-2 primary-color hover:opacity-80 text-sm underline"
              >
                Jetzt laden
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {auditLogs.slice(0, 5).map((log) => (
              <div
                key={log.id}
                className="flex items-center justify-between py-2 border-b dark:border-slate-600"
              >
                <div>
                  <p className="text-sm font-medium dark:text-slate-200">
                    {log.action}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {log.dataSubject} | {log.timestamp}
                  </p>
                </div>
                <span className="text-xs text-gray-400 dark:text-slate-500">
                  {log.performedBy}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTab;
